import { useState, useEffect } from 'react';
import { GameMode } from './core/GameEngine';
import { ModeSelector } from './ui/ModeSelector';
import { InstructionsScreen } from './ui/InstructionsScreen';
import { GameBoard } from './ui/GameBoard';
import { WelcomeScreen } from './ui/WelcomeScreen';
import { TutorialGame } from './tutorial/TutorialGame';
import { ChaosTutorialGame } from './tutorial/ChaosTutorialGame';
import { RhythmTutorialGame } from './tutorial/RhythmTutorialGame';
import { 
  isFirstRun, 
  isFirstRunChaos, 
  isFirstRunRhythm,
  markTutorialCompleted,
  markChaosTutorialCompleted,
  markRhythmTutorialCompleted
} from './utils/storage';

type AppScreen = 'welcome' | 'tutorial-classic' | 'tutorial-chaos' | 'tutorial-rhythm' | 'mode-select' | 'instructions' | 'playing';

function App() {
  const [screen, setScreen] = useState<AppScreen>('mode-select');
  const [selectedMode, setSelectedMode] = useState<GameMode>('classic');

  useEffect(() => {
    // Check if this is the first run
    if (isFirstRun()) {
      setScreen('welcome');
    }
  }, []);

  const handleSelectMode = (mode: GameMode) => {
    setSelectedMode(mode);
    
    // Check if first-run for this mode
    if (mode === 'classic' && isFirstRun()) {
      setScreen('tutorial-classic');
    } else if (mode === 'chaos' && isFirstRunChaos()) {
      setScreen('tutorial-chaos');
    } else if (mode === 'rhythm' && isFirstRunRhythm()) {
      setScreen('tutorial-rhythm');
    } else {
      setScreen('instructions');
    }
  };

  const handleStartGame = () => {
    setScreen('playing');
  };

  const handleBackToModeSelect = () => {
    setScreen('mode-select');
  };

  const handleStartTutorial = (mode: GameMode = 'classic') => {
    if (mode === 'classic') {
      setScreen('tutorial-classic');
    } else if (mode === 'chaos') {
      setScreen('tutorial-chaos');
    } else if (mode === 'rhythm') {
      setScreen('tutorial-rhythm');
    }
  };

  const handleSkipTutorial = (mode: GameMode = 'classic') => {
    if (mode === 'classic') {
      markTutorialCompleted();
    } else if (mode === 'chaos') {
      markChaosTutorialCompleted();
    } else if (mode === 'rhythm') {
      markRhythmTutorialCompleted();
    }
    setScreen('mode-select');
  };

  if (screen === 'welcome') {
    return (
      <WelcomeScreen 
        onStartTutorial={() => handleStartTutorial('classic')}
        onSkipTutorial={() => handleSkipTutorial('classic')}
      />
    );
  }

  if (screen === 'tutorial-classic') {
    return (
      <TutorialGame 
        onComplete={() => {
          markTutorialCompleted();
          setScreen('instructions');
        }}
        onSkip={() => handleSkipTutorial('classic')}
      />
    );
  }

  if (screen === 'tutorial-chaos') {
    return (
      <ChaosTutorialGame 
        onComplete={() => {
          markChaosTutorialCompleted();
          setScreen('instructions');
        }}
        onSkip={() => handleSkipTutorial('chaos')}
      />
    );
  }

  if (screen === 'tutorial-rhythm') {
    return (
      <RhythmTutorialGame 
        onComplete={() => {
          markRhythmTutorialCompleted();
          setScreen('instructions');
        }}
        onSkip={() => handleSkipTutorial('rhythm')}
      />
    );
  }

  if (screen === 'mode-select') {
    return (
      <ModeSelector 
        onSelectMode={handleSelectMode}
        onStartTutorial={handleStartTutorial}
      />
    );
  }

  if (screen === 'instructions') {
    return (
      <InstructionsScreen 
        mode={selectedMode}
        onStartGame={handleStartGame}
        onBack={handleBackToModeSelect}
      />
    );
  }

  return (
    <GameBoard 
      initialMode={selectedMode} 
      onBackToModeSelect={handleBackToModeSelect}
    />
  );
}

export default App;
